import {
  extractBasedOnSymbology,
  isBarcodeDetectorSupported,
} from "@ingka-group-digital/flexa-scanner";
import React, { useCallback, useState } from "react";
import { Item } from "../shared/types/item";
import CameraIconPath from "../shared/wrappers/FixaSSRIcon/paths/CameraIconPath";
import { ProductSearchComponent } from "./pages/product-search-page/ProductSearchComponent";
import { ExtractedArticleInformationType } from "./types/ExtractedArticleInformationType";
import { SearchItemCardRenderType } from "./types/SearchItemCardRenderType";

interface ScanAndSearchSlotProps {
  /** Called when an item card is clicked in the search results. */
  onResult: (result: ExtractedArticleInformationType) => void;
  /** Called when the camera button is pressed. Supply only when camera state is managed externally. */
  onCameraOpen?: () => void;
  /** Suppress SPR items from search results. */
  shouldHideSPRItems?: boolean;
  /** Suppress local combination items from search results. */
  shouldHideLocalCombinationItems?: boolean;
  /** Called on extraction errors. */
  onError?: (message: string, context: Record<string, unknown>) => void;
  /** Renders a checkbox on each result card. */
  multiSelect?: boolean;
  selectedItemNos?: string[];
  onToggleItemSelect?: (item: Item) => void;
}

/**
 * Provides the `searchSlot` content for the package's DefaultScannerComponent.
 * Manages its own search-query state and wires item-card clicks back through onResult.
 */
export const ScanAndSearchSlot: React.FC<ScanAndSearchSlotProps> = ({
  onResult,
  onCameraOpen,
  shouldHideSPRItems = false,
  shouldHideLocalCombinationItems = false,
  onError,
  multiSelect = false,
  selectedItemNos = [],
  onToggleItemSelect,
}) => {
  const [searchQuery, setSearchQuery] = useState("");

  const handleItemCardClick = useCallback(
    (item: Item) => {
      try {
        const extracted = extractBasedOnSymbology(
          "ARTICLE_NUMBER",
          item.no,
          onError
        );
        setSearchQuery("");
        onResult({ ...extracted, articleType: item.type });
      } catch (e) {
        console.error("[Scanner:SearchSlot] extraction failed:", e);
      }
    },
    [onResult, onError]
  );

  const cameraActions =
    onCameraOpen && isBarcodeDetectorSupported()
      ? [{ ssrIcon: CameraIconPath, onClick: onCameraOpen }]
      : undefined;

  return (
    <ProductSearchComponent
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      handleItemCardClick={handleItemCardClick}
      itemCardRenderType={SearchItemCardRenderType.button}
      shouldHideSPRItems={shouldHideSPRItems}
      shouldHideLocalCombinationItems={shouldHideLocalCombinationItems}
      searchBarActions={cameraActions}
      multiSelect={multiSelect}
      selectedItemNos={selectedItemNos}
      onToggleItemSelect={onToggleItemSelect}
    />
  );
};
